/**
 * One LANE of the pool on the formula: rank, its pool letter, the gateway's
 * name with the lane's sender IDs (two lanes of one gateway read apart),
 * volume, the formula's values, the score with its bar, its share when the
 * weights follow the formula, and — opened — how the score is made. A
 * gateway an exclude rule takes out says why instead of a score (never counted as bad).
 * The rules' word on it (Phase 19) rides next to its name.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { type DropGenGatewayLane, type DropGenHourFormula, type DropGenRankedRoute, laneLabel } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import type { RankingColumn } from '../../creative/ranking/results/ranking-columns';
import { rowGrid } from '../../creative/ranking/results/results-grid';
import { ExpandButton, MetricCells, ScoreCell } from '../../creative/ranking/results/rows/RowCells';
import { ScoreDetail } from '../../creative/ranking/results/ScoreDetail';
import { excludeHit } from '../../rules/rule-model';
import { RuleHitBadges, RuleHitList } from '../../rules/RuleHits';
import { TagChips } from '../../rules/TagChips';
import { gwColor, gwLetter } from '../cards/strategy-copy';

const fmt = (n: number) => n.toLocaleString('en-US');

interface Props {
  r: DropGenRankedRoute;
  /** The lane this row stands for (the ranking is the gateway's). */
  lane: Pick<DropGenGatewayLane, 'routeId' | 'senders'>;
  poolIndex: number;
  name: string;
  columns: RankingColumn[];
  formula: DropGenHourFormula;
  bar: number;
  /** Its share in % when the weights follow the formula, else null. */
  share: number | null;
  open: boolean;
  onOpen: () => void;
  floor: { minVolume: number | null; rangeLabel: string; primaryRangeKey: string };
}

export const RouteRow: FC<Props> = ({ r, lane, poolIndex, name, columns, formula, bar, share, open, onOpen, floor }) => (
  <li className={cn(open && 'bg-primary/5')}>
    <div className={cn(rowGrid(columns.length), 'px-3 py-2')}>
      <span className="font-mono text-xs tabular-nums text-muted-foreground">{r.rank ? `#${r.rank}` : '—'}</span>
      <button type="button" onClick={onOpen} className="flex min-w-0 items-center gap-2 text-left">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded font-mono text-[11px] font-bold text-white" style={{ backgroundColor: gwColor(poolIndex) }}>
          {gwLetter(poolIndex)}
        </span>
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-medium">{laneLabel(lane, name)}</span>
            <RuleHitBadges hits={r.ruleHits} />
          </span>
          <span className="block text-[11px] text-muted-foreground">
            #{r.routeId} · {fmt(r.uniqueVisits)} unique visits · {fmt(r.conversions)} conv.{share !== null ? ` · ${Math.round(share)} % of the drops` : ''}
          </span>
          {r.tags?.length ? (
            <span className="mt-1 flex">
              <TagChips tags={r.tags} max={4} />
            </span>
          ) : null}
        </span>
      </button>
      <MetricCells columns={columns} metricsByRange={r.metricsByRange} floor={floor} />
      <ScoreCell score={r.score} bar={bar} />
      <div className="flex justify-end">
        <ExpandButton open={open} onClick={onOpen} what="this gateway" />
      </div>
    </div>
    {open && r.ruleHits?.length ? (
      <div className="px-3 pb-2">
        <RuleHitList hits={r.ruleHits} />
      </div>
    ) : null}
    {open ? (
      <ScoreDetail
        formula={formula}
        breakdown={r.breakdown}
        metricsByRange={r.metricsByRange}
        unrankedNote={`Excluded — ${excludeHit(r.ruleHits)?.reason ?? `no visits through this gateway in ${floor.rangeLabel}`}: not ranked yet, never counted as bad.`}
      />
    ) : null}
  </li>
);
